import * as ko from "knockout";
import * as Global from "../common/global";
import * as UI from "../common/ui";
import * as RegistrationData from "../data/selfRegistrationData";
import * as UserData from "../data/userData";
import { IApplication } from "../interfaces/application";
import * as Data from "../interfaces/data";

interface IViewModel {
    RegistrationCode: KnockoutObservable<string>;
    RegistrationCodeMissing: KnockoutObservable<boolean>;
    IsRegistrationCodeValid: KnockoutObservable<boolean>;
    UserName: KnockoutObservable<string>;
    Email: KnockoutObservable<string>;
    FirstName: KnockoutObservable<string>;
    LastName: KnockoutObservable<string>;
    Password: KnockoutObservable<string>;
    ConfirmPassword: KnockoutObservable<string>;
    IsEditing: KnockoutObservable<boolean>;
    IsActive: KnockoutObservable<boolean>;
    TimeZoneID: KnockoutObservable<number>;
    Roles: KnockoutObservableArray<any>;
    ShowPassword: KnockoutObservable<boolean>;
    IsRegistrationComplete: KnockoutObservable<boolean>;

    // values for drop down lists
    TimeZones: KnockoutObservableArray<Data.ICodeModel<number>>;

    // password requirements
    MinimumPasswordLength: KnockoutObservable<number>;
    PasswordComplexityRequirements: KnockoutObservable<string>;
    ResetPasswordOnNextLogin: KnockoutObservable<boolean>;

    // functions
    passwordVisible: (user: any) => void;
    onIsRegistrationCodeValidForWorkForce: () => void;
    onIsRegistrationCodeValidForOasis: () => void;
    onCancel: () => void;
    onSubmit: () => void;
    onContinue: () => void;
}

export class SelfRegistrationController {
    // Value comes form the Timezone table in the DB
    private readonly EST_TIME_ZONE_ID: number = 234;

    // references to DOM elements
    private viewModel: IViewModel;
    private container: JQuery;
    private fieldsOnLoad: any = {};
    private registrationCriteria: string = "";

    constructor() {
        const me: SelfRegistrationController = this;
        const app: IApplication = Global.app();

        this.viewModel = {
            RegistrationCode: ko.observable(""),
            RegistrationCodeMissing: ko.observable(false),
            IsRegistrationCodeValid: ko.observable(false),
            UserName: ko.observable("").extend({ required: true }),
            Email: ko.observable(""),
            FirstName: ko.observable("").extend({ required: true }),
            LastName: ko.observable("").extend({ required: true }),
            Password: ko.observable(""),
            ConfirmPassword: ko.observable(""),
            IsEditing: ko.observable(false),
            IsActive: ko.observable(true),
            TimeZoneID: ko.observable(0),
            Roles: ko.observableArray(),
            ShowPassword: ko.observable(false),
            IsRegistrationComplete: ko.observable(false),

            // values for drop down lists
            TimeZones: ko.observableArray<Data.ICodeModel<number>>(),

            // password requirements
            MinimumPasswordLength: ko.observable(0),
            PasswordComplexityRequirements: ko.observable(""),
            ResetPasswordOnNextLogin: ko.observable(true),

            passwordVisible(user: any): void {
                user.ShowPassword(!user.ShowPassword());
            },
            onIsRegistrationCodeValidForWorkForce() {
                me.registrationCriteria = RegistrationData.WEST_VIRGINIA_CRITERIA_IN_WORK_FORCE;
                me.isRegistrationCodeValid(me.registrationCriteria);
            },
            onIsRegistrationCodeValidForOasis() {
                me.registrationCriteria = RegistrationData.WEST_VIRGINIA_CRITERIA_IN_OASIS;
                me.isRegistrationCodeValid(me.registrationCriteria);
            },
            onCancel(): void {
                app.pageManager().closeCurrentPage();
            },
            onSubmit(): void {
                me.submitForm();
            },
            onContinue(): void {
                app.pageManager().closeCurrentPage();
            },
        };

        this.viewModel.Password = ko.observable("").extend({
            required: {
                message: "Please enter a password",
                params: true,
                onlyIf: me.viewModel.ShowPassword,
            },
        });

        this.viewModel.ConfirmPassword = ko.observable("").extend({
            required: {
                message: "Please confirm password",
                params: true,
                onlyIf: me.viewModel.ShowPassword,
            },
        });
    }

    public render(container: JQuery): JQueryPromise<any> {
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();

        this.container = container;
        ko.applyBindings(this.viewModel, container[0]);
        this.loadPasswordRequirements();
        this.getTimeZones();

        deferred.resolve();
        return deferred.promise();
    }

    public load(params: any[]): JQueryPromise<any> {
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();

        const app = Global.app();
        if (app.isLoggedIn) {
            // This page should only be used if the user is not logged in
            app.pageManager().closeAllAndGotoDefaultPage();
        }

        deferred.resolve();
        return deferred.promise();
    }

    public appRouterID(params: any[]): string {
        return "SelfRegistration";
    }

    public title(): string {
        return "Self Registration";
    }

    public subTitle(): string {
        return "";
    }

    public AllowAnonymousAccess(): boolean {
        return true;
    }

    public haveFieldsChanged(): boolean {
        const fieldValues: string = ko.toJSON(this.viewModel);
        return fieldValues !== this.fieldsOnLoad;
    }

    private isRegistrationCodeValid(registrationCriteria: string): void {
        const me: SelfRegistrationController = this;
        const targetContainer: JQuery<HTMLElement> = this.container.find("div[data-id='reg_code']");
        const waitContainer: JQuery<HTMLElement> = UI.showWaitIndicator(targetContainer);
        const request: RegistrationData.IRegistrationCodeRequest = {
            RegistrationCode: this.viewModel.RegistrationCode(),
            RegistrationCriteria: registrationCriteria,
        };

        if (!this.isRegistrationCodeEntered()) {
            me.setFocusToRegistrationCodeField();
            UI.hideWaitIndicator(targetContainer, waitContainer);
            return;
        }

        RegistrationData.ValidateRegistrationCode(request)
            .done((response: Data.IResponse<RegistrationData.IUserSelfRegistrationValidateResponse>) => {
                if (response.Success) {
                    me.viewModel.IsRegistrationCodeValid(response.Payload.IsValid);
                    if (!me.viewModel.IsRegistrationCodeValid()) {
                        UI.showErrorMessage([response.Payload.ValidationMessage]);
                    }
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                }
            })
            .always(() => {
                UI.hideWaitIndicator(targetContainer, waitContainer);
            });
    }

    private submitForm(): JQueryPromise<any> {
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();

        if (this.isValid()) {
            const targetContainer: JQuery<HTMLElement> = this.container.find("form[data-id='reg_form']");
            const waitContainer: JQuery<HTMLElement> = UI.showWaitIndicator(targetContainer);

            const request: RegistrationData.ICreateUserRequest = {
                RegistrationCode: this.viewModel.RegistrationCode(),
                RegistrationCriteria: this.registrationCriteria,
                UserName: this.viewModel.UserName(),
                Password: this.viewModel.Password(),
                ConfirmPassword: this.viewModel.ConfirmPassword(),
                Email: this.viewModel.Email(),
                FirstName: this.viewModel.FirstName(),
                LastName: this.viewModel.LastName(),
                TimeZoneID: this.EST_TIME_ZONE_ID, // Hardcoding timezone per West Virginia request
            };

            RegistrationData.CreateUser(request)
                .done((response: Data.IResponse<any>) => {
                    if (response.Success) {
                        deferred.resolve();
                        targetContainer.hide("fast");
                        this.viewModel.IsRegistrationComplete(true);
                    } else {
                        UI.showErrorMessage(response.ErrorMessages);
                        deferred.reject();
                    }
                })
                .always(() => {
                    UI.hideWaitIndicator(targetContainer, waitContainer);
                });
        } else {
            // not all valid fields are entered
            deferred.reject();
        }
        return deferred.promise();
    }

    private isRegistrationCodeEntered(): boolean {
        return UI.setRequiredStringError(this.viewModel.RegistrationCode(), "RegistrationCodeMissing", this.viewModel);
    }

    private isValid(): boolean {
        const view: IViewModel = this.viewModel;

        if (view.Password() !== view.ConfirmPassword()) {
            UI.showErrorMessage(["Passwords do not match"]);
            return false;
        }

        return true;
    }

    private setFocusToRegistrationCodeField(): void {
        this.container.find("input[data-id='reg_code']").first().focus();
    }

    private getTimeZones(): void {
        const me: SelfRegistrationController = this;
        const timezones: Array<Data.ICodeModel<number>> = Global.app().global.TimeZones;
        me.viewModel.TimeZones.push.apply(me.viewModel.TimeZones, timezones);
    }

    private loadPasswordRequirements(): void {
        const me: SelfRegistrationController = this;

        UserData.GetPasswordRequirements()
            .done((response: Data.IResponse<UserData.IPasswordRequirementsResponse>) => {
                if (response.Success) {
                    me.viewModel.MinimumPasswordLength(response.Payload.MinimumPasswordLength);
                    me.viewModel.PasswordComplexityRequirements(response.Payload.PasswordComplexityRequirements);
                }
            });
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--7!./src/script/controllers/selfRegistration.ts